import React, { useState } from "react";
import { Server, LineChart, Trash2, KeyRound, Save } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { Panel, Overline } from "../common/Panel";

const ADMIN_EMAILS = ["henockhnk092@gmail.com", "admin@test.com"];

const inputCls = "w-full bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-[var(--accent)] transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[var(--t2)]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const EMPTY_KEYS = {
  geminiKeys: "", deepseekKey: "", elevenKey: "", elevenVoiceId: "",
  azureKey: "", azureRegion: "", azureVoice: "", voicerssKey: "", voicerssLang: "", voicerssVoice: "",
};

export default function SettingsTab() {
  const { settings: s, updateSettings, resetSettings, reconnect } = useApp();
  const { user } = useAuth();
  const isAdmin = ADMIN_EMAILS.includes(user?.email);
  const [keyDraft, setKeyDraft] = useState(EMPTY_KEYS);
  const [keySaved, setKeySaved] = useState(false);

  const handleKeySave = () => {
    const patch = {};
    Object.entries(keyDraft).forEach(([k, v]) => { if (v.trim()) patch[k] = v.trim(); });
    if (Object.keys(patch).length) updateSettings(patch);
    setKeyDraft(EMPTY_KEYS);
    setKeySaved(true);
    setTimeout(() => setKeySaved(false), 2000);
  };
  const setKey = (k) => (e) => setKeyDraft((d) => ({ ...d, [k]: e.target.value }));

  return (
    <div className="space-y-6">
      <div>
        <Overline>Configuration</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Settings</h2>
      </div>

      {/* MQTT */}
      <Panel className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Server size={16} className="text-[var(--accent)]" />
          <Overline>MQTT Broker (HiveMQ Cloud)</Overline>
        </div>
        <Field label="Host">
          <input data-testid="set-mqtt-host" className={inputCls} value={s.mqttHost} onChange={(e) => updateSettings({ mqttHost: e.target.value })} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Username">
            <input data-testid="set-mqtt-user" className={inputCls} value={s.mqttUser} onChange={(e) => updateSettings({ mqttUser: e.target.value })} />
          </Field>
          <Field label="Password">
            <input data-testid="set-mqtt-pass" className={inputCls} type="password" value={s.mqttPass} onChange={(e) => updateSettings({ mqttPass: e.target.value })} placeholder="password" />
          </Field>
        </div>
        <div className="text-xs text-[var(--t2)] font-mono space-y-0.5">
          <div>ESP32 → port <span className="text-[var(--accent)]">8883</span> (MQTT over TLS)</div>
          <div>Browser → port <span className="text-[var(--accent)]">8884</span> (WebSocket Secure)</div>
          <div>Topics: <span className="text-[var(--accent)]">campus/energy/pzem</span> · campus/energy/pzem_cmd · campus/camera/*</div>
        </div>
        <button data-testid="set-mqtt-save" onClick={() => reconnect(s.mqttHost, s.mqttUser, s.mqttPass)}
          className="px-4 py-2 rounded-md bg-[var(--accent)] text-black text-sm font-medium active:scale-95 transition-transform">
          Save &amp; Reconnect
        </button>
      </Panel>

      {/* Grafana */}
      <Panel className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <LineChart size={16} className="text-[var(--accent)]" />
          <Overline>Grafana Dashboard</Overline>
        </div>
        <Field label="Dashboard URL">
          <input data-testid="set-grafana-url" className={inputCls} value={s.grafanaUrl} onChange={(e) => updateSettings({ grafanaUrl: e.target.value })} />
        </Field>
        <p className="text-xs text-[var(--t2)] font-mono">Embedded in the Grafana tab via iframe</p>
      </Panel>

      {/* AI & Voice API Keys — admin only */}
      {isAdmin && (
        <Panel className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <KeyRound size={16} className="text-[var(--accent)]" />
            <Overline>AI &amp; Voice API Keys</Overline>
            <span className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded-full bg-[var(--accent-soft)] text-[var(--accent)]">Admin only</span>
          </div>
          <p className="text-xs text-[var(--t2)] font-mono">Leave a field blank to keep the existing key. Values are never shown.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Gemini API Keys (one per line)">
              <textarea rows={3} className={inputCls + " resize-none"} type="password" placeholder="Enter to replace..." value={keyDraft.geminiKeys} onChange={setKey("geminiKeys")} autoComplete="off" />
            </Field>
            <Field label="DeepSeek API Key">
              <input className={inputCls} type="password" placeholder="Enter to replace..." value={keyDraft.deepseekKey} onChange={setKey("deepseekKey")} autoComplete="off" />
            </Field>
            <Field label="ElevenLabs API Key">
              <input className={inputCls} type="password" placeholder="Enter to replace..." value={keyDraft.elevenKey} onChange={setKey("elevenKey")} autoComplete="off" />
            </Field>
            <Field label="ElevenLabs Voice ID">
              <input className={inputCls} type="password" placeholder="Enter to replace..." value={keyDraft.elevenVoiceId} onChange={setKey("elevenVoiceId")} autoComplete="off" />
            </Field>
            <Field label="Azure Speech Key">
              <input className={inputCls} type="password" placeholder="Enter to replace..." value={keyDraft.azureKey} onChange={setKey("azureKey")} autoComplete="off" />
            </Field>
            <Field label="Azure Region">
              <input className={inputCls} placeholder="Enter to replace..." value={keyDraft.azureRegion} onChange={setKey("azureRegion")} autoComplete="off" />
            </Field>
            <Field label="Azure Voice">
              <input className={inputCls} placeholder="Enter to replace..." value={keyDraft.azureVoice} onChange={setKey("azureVoice")} autoComplete="off" />
            </Field>
            <Field label="VoiceRSS Key">
              <input className={inputCls} type="password" placeholder="Enter to replace..." value={keyDraft.voicerssKey} onChange={setKey("voicerssKey")} autoComplete="off" />
            </Field>
          </div>
          <button onClick={handleKeySave}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--accent)] text-black text-sm font-medium active:scale-95 transition-transform">
            <Save size={14} /> {keySaved ? "Saved" : "Save API Keys"}
          </button>
        </Panel>
      )}

      {/* Danger Zone */}
      <Panel className="p-6 border-[var(--danger)]/40">
        <Overline className="text-[var(--danger)]">Danger Zone</Overline>
        <p className="text-sm text-[var(--t2)] mt-2 mb-3">Reset all configuration to factory defaults.</p>
        <button data-testid="set-reset-all" onClick={resetSettings}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[var(--danger)]/50 text-[var(--danger)] text-sm font-medium hover:bg-[var(--danger)]/10 transition-colors">
          <Trash2 size={15} /> Reset All Settings
        </button>
      </Panel>
    </div>
  );
}
